import {
  buildSearchIndex,
  childKey,
  searchNav,
} from '../../Nav/navSearch';

export { buildSearchIndex, childKey, searchNav };

/**
 * Item/child `description` strings render as a single line in the sidebar (no wrapping,
 * no truncation) at a fixed 300px sidebar width — see .seller-nav-item__description and
 * .seller-nav-item__child-description in SellerNav.css. Keep them to ~38 characters or
 * fewer (~30 for an item that also shows a disclosure button, i.e. has children or
 * contextualNotes, which eats extra horizontal space) so they never wrap to a second line.
 *
 * @typedef {Object} SellerNavChild
 * @property {string} id
 * @property {string} title
 * @property {string} description
 * @property {string} href
 * @property {string} icon
 * @property {string[]} [keywords]
 */

/**
 * @typedef {Object} SellerNavContextualNote
 * @property {string} label
 */

/**
 * @typedef {Object} SellerNavItem
 * @property {string} id
 * @property {string} title
 * @property {string} description
 * @property {string} href
 * @property {string} icon
 * @property {string} helpQuery
 * @property {'orders'|'reviews'|'messages'} [alertKey]
 * @property {string[]} [keywords]
 * @property {SellerNavChild[]} [children]
 * @property {SellerNavContextualNote[]} [contextualNotes]
 * @property {string[]} [planTypes]
 * @property {(context: SellerNavVisibilityContext) => boolean} [isVisible]
 */

/**
 * @typedef {Object} SellerNavSection
 * @property {string} id
 * @property {string} title
 * @property {string} description
 * @property {string} icon
 * @property {SellerNavItem[]} items
 * @property {boolean} [isPinned] Render the items as flat, always-visible rows rather than
 *   behind an accordion header. Everything else about the section is unchanged.
 */

/**
 * @typedef {Object} SellerNavVisibilityContext
 * @property {Object} channel
 * @property {Object} account
 */

export const SELLER_NAV_SECTIONS = [
  {
    id: 'dashboard',
    title: 'Dashboard',
    description: 'Start here',
    icon: 'fa-solid fa-house',
    // Rendered as flat always-visible rows instead of an accordion — the dashboard is the
    // most-visited destination in the sidebar and shouldn't cost an expand to reach. It stays an
    // ordinary section otherwise, so search, visibility gating, and active-resolution need no
    // special case for it.
    isPinned: true,
    items: [
      {
        id: 'dashboard-home',
        title: 'Dashboard Home',
        description: 'Always a good place to start.',
        href: '/account/dashboard',
        icon: 'fa-solid fa-house',
        helpQuery: 'seller dashboard home overview',
      },
    ],
  },
  {
    id: 'website-storefront',
    title: 'Website & Storefront',
    description: 'Shape your storefront and content',
    icon: 'fa-solid fa-store',
    items: [
      {
        id: 'storefront-settings',
        title: 'Storefront Settings',
        description: 'Address, imagery, basics, and location',
        href: '/account/landingpage',
        icon: 'fa-solid fa-sliders',
        helpQuery: 'storefront setup settings',
        children: [
        ],
      },
      {
        id: 'custom-pages',
        title: 'Custom Pages',
        description: 'Info pages, portfolios, and policies',
        href: '/account/pages',
        icon: 'fa-solid fa-file-lines',
        helpQuery: 'custom storefront pages',
      },
      {
        id: 'links-socials',
        title: 'Links & Friends',
        description: 'Highlight links and other creators',
        href: '/account/landingpage/links',
        icon: 'fa-solid fa-link',
        helpQuery: 'storefront links friends',
      },
      {
        id: 'social-profiles',
        title: 'Social Profiles',
        description: 'Connect your social media accounts',
        href: '/account/landingpage/socials',
        icon: 'fa-solid fa-share-nodes',
        helpQuery: 'storefront social profiles',
      },
      {
        id: 'blog',
        title: 'Blog',
        description: 'Publish updates for your audience',
        href: '/account/blog',
        icon: 'fa-solid fa-pen-nib',
        helpQuery: 'write blog post',
        isVisible: ({ channel }) => {
          const planType = channel?.planType || channel?.plan?.type;
          return ['free-forever', 'monthly', 'year', 'cc-monthly', 'cc-year'].includes(planType);
        },
      },
      {
        id: 'storefront-analytics',
        title: 'Storefront Analytics',
        description: 'Explore pageviews and traffic trends',
        href: '/account/analytics',
        icon: 'fa-solid fa-chart-area',
        helpQuery: 'storefront analytics pageviews',
      },
    ],
  },
  {
    id: 'manage-products',
    title: 'Manage Products',
    description: 'Organize your catalog',
    icon: 'fa-solid fa-boxes-stacked',
    items: [
      {
        id: 'product-catalog',
        title: 'Product Catalog',
        description: 'Edit, sort, and publish your products',
        href: '/account/products',
        icon: 'fa-solid fa-box-open',
        helpQuery: 'manage products',
        children: [ ],
      },
      {
        id: 'folders',
        title: 'Folders',
        description: 'Group products into collections',
        href: '/account/products/folders',
        icon: 'fa-solid fa-folder-tree',
        helpQuery: 'product folders',
      },
      {
        id: 'coupons',
        title: 'Coupons',
        description: 'Create and manage product discounts',
        href: '/account/products/coupons',
        icon: 'fa-solid fa-tags',
        helpQuery: 'product coupons discounts',
      },
      {
        id: 'bookings',
        title: 'Bookings & Scheduling',
        description: 'Manage availability and reservations',
        href: '/account/bookings',
        icon: 'fa-solid fa-calendar-check',
        helpQuery: 'bookings scheduling availability',
      },
    ],
  },
  {
    id: 'invoices-customers',
    title: 'Invoices & Customers',
    description: 'Bill and manage customers',
    icon: 'fa-solid fa-file-invoice-dollar',
    items: [
      {
        id: 'manage-invoices',
        title: 'Create & Manage Invoices',
        description: 'Send invoices and track balances',
        href: '/account/invoices',
        icon: 'fa-solid fa-file-circle-plus',
        helpQuery: 'create manage invoices',
      },
      {
        id: 'customer-directory',
        title: 'Customer Directory',
        description: 'Browse customer details and history',
        href: '/account/subscribers',
        icon: 'fa-solid fa-address-book',
        helpQuery: 'customers subscribers',
      },
      {
        id: 'messages',
        title: 'Customer Messages',
        description: 'Communicate with customers',
        href: '/account/messages',
        icon: 'fa-solid fa-envelope',
        helpQuery: 'customer messages',
        alertKey: 'messages',
      },
      {
        id: 'newsletters',
        title: 'Email Newsletters',
        description: 'Write and send to opted-in subscribers',
        href: '/account/newsletters',
        icon: 'fa-solid fa-paper-plane',
        helpQuery: 'email newsletters',
        keywords: ['newsletter', 'email', 'blast', 'subscribers', 'mailing list'],
      },
    ],
  },
  {
    id: 'orders-fulfillment',
    title: 'Orders & Fulfillment',
    description: 'Fulfill sales and manage logistics',
    icon: 'fa-solid fa-cart-shopping',
    items: [
      {
        id: 'manage-orders',
        title: 'Orders',
        description: 'Track payments and fulfillment status',
        href: '/account/orders',
        icon: 'fa-solid fa-clipboard-list',
        helpQuery: 'manage orders',
        alertKey: 'orders',
        children: [
        ],
      },
      {
        id: 'reviews',
        title: 'Reviews',
        description: 'Read and reply to product reviews',
        href: '/account/reviews',
        icon: 'fa-solid fa-star',
        alertKey: 'reviews',
      },
      {
        id: 'tax',
        title: 'Tax Center',
        description: 'Review and download tax reports',
        href: '/account/orders/tax-center',
        icon: 'fa-solid fa-percent',
      },
      {
        id: 'labels',
        title: 'Print Labels',
        description: 'Buy and print shipping labels',
        href: '/account/orders/labels',
        icon: 'fa-solid fa-print',
      },
    ],
  },
  {
    id: 'payments-payouts',
    title: 'Payments & Payouts',
    description: 'Configure payments and manage earnings',
    icon: 'fa-solid fa-money-check-dollar',
    items: [
      {
        id: 'accept-payments',
        title: 'Accept Payments',
        description: 'Configure how customers pay you',
        href: '/account/payments/links',
        icon: 'fa-solid fa-credit-card',
        helpQuery: 'accept payments payment methods',
      },
      {
        id: 'payout-profile',
        title: 'Payout Profile',
        description: 'Manage your identity and bank details',
        href: '/account/payments/profile',
        icon: 'fa-solid fa-id-card',
        helpQuery: 'payout profile',
        children: [
        ],
      },
      {
        id: 'payouts',
        title: 'Payouts',
        description: 'Review history or request a payout',
        href: '/account/payments/payouts',
        icon: 'fa-solid fa-money-bill-transfer',
        helpQuery: 'seller payouts',
      },
      {
        id: 'affiliate-codes',
        title: 'Affiliate Codes',
        description: 'Create codes and track commissions',
        href: '/account/affiliates',
        icon: 'fa-solid fa-people-arrows',
        helpQuery: 'affiliate codes seller referrals',
      },
    ],
  },
  {
    id: 'account-settings',
    title: 'Account & Settings',
    description: 'Manage your account and preferences',
    icon: 'fa-solid fa-gear',
    items: [
      {
        id: 'billing-beta',
        title: 'Billing (Beta)',
        description: 'Preview the new combined billing page',
        href: '/account/billing',
        icon: 'fa-solid fa-file-invoice',
        helpQuery: 'seller billing dashboard beta',
        // Server-resolved mode projected onto the channel by presence.json — see
        // channelPlanPresentation.js. Never re-derive the env/override resolution here.
        isVisible: ({ channel }) => Boolean(channel?.sellerBillingDashboardMode)
          && channel.sellerBillingDashboardMode !== 'off',
      },
      {
        id: 'plan-upgrades',
        title: 'Plan & Upgrades',
        description: 'Manage your subscription plan',
        href: '/account/plan',
        icon: 'fa-solid fa-chart-line',
        helpQuery: 'seller plan subscription',
        keywords: ['cancel', 'cancel subscription', 'downgrade', 'unsubscribe'],
      },
      {
        id: 'billing-history',
        title: 'Billing History',
        description: 'Review charges on your Mayhem plan',
        href: '/account/payments/billing-history',
        icon: 'fa-solid fa-receipt',
        helpQuery: 'billing history subscription',
      },
      {
        id: 'email-password',
        title: 'Email & Password',
        description: 'Manage login credentials and access',
        href: '/account/profile',
        icon: 'fa-solid fa-lock',
        helpQuery: 'change password email',
      },
      {
        id: 'preferences',
        title: 'Preferences',
        description: 'Manage notifications and settings',
        href: '/account/profile/preferences',
        icon: 'fa-solid fa-sliders',
        helpQuery: 'account preferences',
      },
      {
        id: 'verification-center',
        title: 'Verification Center',
        description: 'Manage your Verified Seller status',
        href: '/account/verification',
        icon: 'fa-solid fa-circle-check',
        helpQuery: 'verified seller verification',
      },
      {
        id: 'compliance',
        title: 'Compliance (2257)',
        description: 'Manage required 2257 records',
        href: '/account/compliance',
        icon: 'fa-solid fa-shield-halved',
        helpQuery: 'seller 2257 compliance',
        isVisible: ({ channel }) => Boolean(channel?.isRed),
      },
      {
        id: 'integrations',
        title: 'Integrations',
        description: 'Connect external services',
        href: '/account/integrations',
        icon: 'fa-solid fa-plug',
        helpQuery: 'seller integrations',
        children: [
          {
            id: 'qr-code',
            title: 'QR Code Generator',
            description: 'Scannable code to your storefront',
            href: '/account/integrations#qr-code-generator',
            icon: 'fa-solid fa-qrcode',
          },
          {
            id: 'printify',
            title: 'Printify',
            description: 'Import print-on-demand products',
            href: '/account/integrations/printify/connect',
            icon: 'fa-solid fa-shirt',
          },
        ],
      },
      {
        id: 'custom-domain',
        title: 'Custom Domain',
        description: 'Connect your own custom domain',
        href: '/account/integrations/custom-domain',
        icon: 'fa-solid fa-globe',
        helpQuery: 'custom storefront domain',
        isVisible: ({ channel }) => Boolean(channel?.metadata?.allowsCustomDomain),
      },
      {
        id: 'media-library',
        title: 'Media Library',
        description: 'Upload and manage reusable media',
        href: '/account/media',
        icon: 'fa-solid fa-photo-film',
        helpQuery: 'media library uploads',
      },
    ],
  },
  {
    id: 'help-learn',
    title: 'Learn and Get Help',
    description: 'Find answers and get support',
    icon: 'fa-solid fa-graduation-cap',
    items: [
      {
        id: 'help-center',
        title: 'Search Help Center',
        description: 'Guidance selected for Mayhem sellers',
        href: '/help',
        icon: 'fa-solid fa-book-open',
        helpQuery: 'help center',
        // Help Center is a separate site outside the dashboard shell — opening it in place would
        // navigate the seller away from their in-progress dashboard work.
        openInNewTab: true,
      },
      {
        id: 'support-tickets',
        title: 'Support Tickets',
        description: 'Review your support conversations',
        href: '/account/help#support-tickets',
        icon: 'fa-solid fa-ticket',
        helpQuery: 'seller support tickets',
      },
      {
        id: 'seller-forums',
        title: 'Seller Forums',
        description: 'Connect with sellers and discussions',
        href: '/account/help/forums',
        icon: 'fa-solid fa-comments',
        helpQuery: 'seller forums community',
      },
      {
        id: 'roadmap',
        title: 'Updates & Roadmap',
        description: "See what's shipping and what's coming",
        href: '/roadmap',
        icon: 'fa-solid fa-map',
        helpQuery: 'Mayhem updates roadmap',
        openInNewTab: true,
      },
    ],
  },
];

export function isNavEntryVisible(entry, context) {
  if (Array.isArray(entry.planTypes) && !entry.planTypes.includes(context.channel?.planType)) {
    return false;
  }
  if (typeof entry.isVisible === 'function' && !entry.isVisible(context)) {
    return false;
  }
  return true;
}

export function filterVisibleSections(sections, context) {
  return sections
    .map(section => ({
      ...section,
      items: section.items
        .filter(item => isNavEntryVisible(item, context))
        .map(item => ({
          ...item,
          children: item.children?.filter(child => isNavEntryVisible(child, context)),
        })),
    }))
    .filter(section => section.items.length > 0);
}
